import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import QRCode from "qrcode";
import SchoolLogo from "./savationChristLogo.png";
import AvatarStudent from "./studentavv.png";

/* =========================================================
   SCHOOL BRANDING
========================================================= */

const SCHOOL_NAME = "Salvation In Christ School System";
const SCHOOL_ADDRESS =
  "Zazay Community, Paynesville City, Monrovia";

const SCHOOL_CONTACT =
  "Contact: +231-776-494-010 / +231-880-585-855 / +231-770-540-258";

  

  

const COLORS = {
  navy: "#062A4B",
  deepBlue: "#063A6B",
  black: "#0A0F12",
  gold: "#F6C944",
  goldenYellow: "#EFA81C",
  orange: "#C0750D",
  white: "#FFFFFF",
  paper: "#F5F8FB",
  border: "#D5DFE8",
  text: "#172B3B",
  muted: "#647586",
  green: "#287A35",
  paleGold: "#FFF8E5",
  paleBlue: "#EDF4FA",
  paleGreen: "#EFF7EA",
};

/* =========================================================
   PAYMENT HELPERS
========================================================= */

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0.00";
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "0.00";
  }

  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getPaymentCurrency = (record) =>
  record?.payment_currency === "USD" ? "USD" : "LRD";

const getCurrencyLabel = (currency) =>
  currency === "USD" ? "USD" : "LD";

const getExchangeRate = (record) =>
  Number(record?.exchange_rate || 0);

const getOriginalPaymentAmount = (record) => {
  if (
    record?.amount_paid !== undefined &&
    record?.amount_paid !== null
  ) {
    return Number(record.amount_paid || 0);
  }

  return Number(record?.amount || 0);
};

const getNormalizedLrdAmount = (record) => {
  const amountPaidLrd = Number(record?.amount_paid_lrd || 0);

  if (amountPaidLrd > 0) {
    return amountPaidLrd;
  }

  const currency = getPaymentCurrency(record);
  const originalAmount = getOriginalPaymentAmount(record);
  const exchangeRate = getExchangeRate(record);

  if (currency === "USD" && exchangeRate > 0) {
    return originalAmount * exchangeRate;
  }

  return originalAmount;
};

const formatCurrency = (amount, currency = "LRD") =>
  `${getCurrencyLabel(currency)} ${formatMoney(amount)}`;

const formatLrd = (amount) => `LD ${formatMoney(amount)}`;

const formatAllocation = (
  allocation,
  paymentCurrency,
  exchangeRate
) => {
  if (!allocation) {
    return "";
  }

  const feeType =
    allocation.fee_type || allocation.name || "Fee";

  if (allocation.amount_display) {
    return `${feeType}: ${allocation.amount_display}`;
  }

  const amountLrd = Number(
    allocation.amount_lrd ?? allocation.amount ?? 0
  );

  if (paymentCurrency === "USD" && exchangeRate > 0) {
    return `${feeType}: USD ${formatMoney(
      amountLrd / exchangeRate
    )}`;
  }

  return `${feeType}: LD ${formatMoney(amountLrd)}`;
};

/* =========================================================
   PDF STYLES
   Two compact receipt copies fit on one A4 page.
========================================================= */

const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 19,
    paddingBottom: 17,
    paddingHorizontal: 25,
    backgroundColor: COLORS.white,
    fontFamily: "Helvetica",
    color: COLORS.text,
  },

  receipt: {
    border: `1 solid ${COLORS.border}`,
    borderRadius: 5,
    padding: 10,
    backgroundColor: COLORS.white,
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 7,
    borderBottom: `2 solid ${COLORS.navy}`,
  },

  logo: {
    width: 43,
    height: 43,
    objectFit: "contain",
    marginRight: 9,
  },

  schoolInfo: {
    flex: 1,
  },

  schoolName: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 3,
  },

  schoolAddress: {
    fontSize: 6.5,
    color: COLORS.muted,
    lineHeight: 1.25,
    marginBottom: 2,
  },

  schoolContact: {
    fontSize: 6.5,
    color: COLORS.muted,
  },

  receiptBadge: {
    backgroundColor: COLORS.navy,
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderRadius: 3,
    marginLeft: 5,
  },

  receiptBadgeText: {
    color: COLORS.gold,
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
  },

  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 6,
    borderBottom: `1 solid ${COLORS.border}`,
    marginBottom: 7,
  },

  copyTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: COLORS.navy,
    letterSpacing: 0.7,
  },

  installment: {
    fontSize: 7.5,
    color: COLORS.deepBlue,
    fontWeight: "bold",
  },

  studentSection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 7,
    borderBottom: `1 solid ${COLORS.border}`,
    marginBottom: 7,
  },

  studentDetails: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  avatar: {
    width: 43,
    height: 43,
    borderRadius: 22,
    objectFit: "cover",
    marginRight: 8,
  },

  studentInfo: {
    flex: 1,
  },

  studentName: {
    fontSize: 9.5,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 4,
  },

  studentLine: {
    fontSize: 7.2,
    color: COLORS.text,
    marginBottom: 2,
  },

  label: {
    fontWeight: "bold",
    color: COLORS.deepBlue,
  },

  qrCode: {
    width: 49,
    height: 49,
    objectFit: "contain",
    marginLeft: 8,
  },

  detailsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    borderTop: `1 solid ${COLORS.border}`,
    borderLeft: `1 solid ${COLORS.border}`,
    marginBottom: 7,
  },

  detailCell: {
    width: "50%",
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderRight: `1 solid ${COLORS.border}`,
    borderBottom: `1 solid ${COLORS.border}`,
  },

  detailLabel: {
    fontSize: 6.5,
    color: COLORS.muted,
    marginBottom: 2,
  },

  detailValue: {
    fontSize: 7.5,
    color: COLORS.text,
    fontWeight: "bold",
  },

  servicesBox: {
    padding: 6,
    backgroundColor: COLORS.paper,
    borderLeft: `3 solid ${COLORS.goldenYellow}`,
    marginBottom: 7,
  },

  servicesLabel: {
    fontSize: 7,
    color: COLORS.navy,
    fontWeight: "bold",
    marginBottom: 3,
  },

  servicesText: {
    fontSize: 7,
    color: COLORS.text,
    lineHeight: 1.35,
  },

  amountSection: {
    flexDirection: "row",
    marginBottom: 7,
  },

  amountBox: {
    flex: 1,
    border: `1 solid ${COLORS.border}`,
    borderRadius: 3,
    paddingVertical: 6,
    paddingHorizontal: 3,
    marginRight: 5,
    alignItems: "center",
    justifyContent: "center",
  },

  amountBoxLast: {
    marginRight: 0,
  },

  amountDue: {
    backgroundColor: COLORS.paleBlue,
    borderColor: COLORS.deepBlue,
  },

  amountPaid: {
    backgroundColor: COLORS.navy,
    borderColor: COLORS.navy,
  },

  amountBalance: {
    backgroundColor: COLORS.paleGold,
    borderColor: COLORS.goldenYellow,
  },

  amountLabel: {
    fontSize: 6.5,
    color: COLORS.muted,
    fontWeight: "bold",
    marginBottom: 4,
  },

  amountLabelPaid: {
    color: COLORS.gold,
  },

  amountValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
  },

  amountValuePaid: {
    color: COLORS.white,
  },

  amountSubValue: {
    fontSize: 6.5,
    color: COLORS.white,
    marginTop: 3,
    textAlign: "center",
  },

  footer: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    paddingTop: 5,
    borderTop: `1 solid ${COLORS.border}`,
  },

  footerText: {
    fontSize: 6.5,
    color: COLORS.muted,
    lineHeight: 1.4,
    flex: 1,
  },

  signature: {
    width: 115,
    marginLeft: 8,
    alignItems: "center",
  },

  signatureLine: {
    width: "100%",
    borderBottom: `1 solid ${COLORS.navy}`,
    marginBottom: 3,
  },

  signatureText: {
    fontSize: 6.5,
    color: COLORS.deepBlue,
    textAlign: "center",
  },

  reference: {
    fontSize: 6,
    color: COLORS.muted,
    marginTop: 5,
  },

  officialFooter: {
    fontSize: 5.8,
    color: COLORS.muted,
    textAlign: "center",
    marginTop: 4,
  },

  divider: {
    borderBottom: `1 dashed ${COLORS.muted}`,
    marginVertical: 10,
  },
});

/* =========================================================
   PDF RECEIPT COPY
========================================================= */

const ReceiptCopyPDF = ({
  copyType,
  receiptData,
  qrCodeUrl,
}) => {
  return (
    <View style={pdfStyles.receipt} wrap={false}>
      <View style={pdfStyles.header}>
        <Image src={SchoolLogo} style={pdfStyles.logo} />

        <View style={pdfStyles.schoolInfo}>
          <Text style={pdfStyles.schoolName}>
            {SCHOOL_NAME}
          </Text>

          <Text style={pdfStyles.schoolAddress}>
            {SCHOOL_ADDRESS}
          </Text>

          <Text style={pdfStyles.schoolContact}>
            Contact: {SCHOOL_CONTACT}
          </Text>
        </View>

        <View style={pdfStyles.receiptBadge}>
          <Text style={pdfStyles.receiptBadgeText}>
            OFFICIAL{"\n"}RECEIPT
          </Text>
        </View>
      </View>

      <View style={pdfStyles.titleRow}>
        <Text style={pdfStyles.copyTitle}>{copyType}</Text>

        <Text style={pdfStyles.installment}>
          {receiptData.last_installment || "Payment"} PAYMENT
        </Text>
      </View>

      <View style={pdfStyles.studentSection}>
        <View style={pdfStyles.studentDetails}>
          <Image
            src={receiptData.profile_image || AvatarStudent}
            style={pdfStyles.avatar}
          />

          <View style={pdfStyles.studentInfo}>
            <Text style={pdfStyles.studentName}>
              {receiptData.student_name || "Student"}
            </Text>

            <Text style={pdfStyles.studentLine}>
              <Text style={pdfStyles.label}>Class: </Text>
              {receiptData.grade_class_name || "N/A"}
            </Text>

            <Text style={pdfStyles.studentLine}>
              <Text style={pdfStyles.label}>Student Type: </Text>
              {receiptData.student_type || "N/A"}
            </Text>

            <Text style={pdfStyles.studentLine}>
              <Text style={pdfStyles.label}>Yearly Fee: </Text>
              {receiptData.yearly_fee}
            </Text>

            <Text style={pdfStyles.studentLine}>
              <Text style={pdfStyles.label}>Overall Balance: </Text>
              {receiptData.balance_fee}
            </Text>
          </View>
        </View>

        {qrCodeUrl ? (
          <Image src={qrCodeUrl} style={pdfStyles.qrCode} />
        ) : null}
      </View>

      <View style={pdfStyles.detailsGrid}>
        <View style={pdfStyles.detailCell}>
          <Text style={pdfStyles.detailLabel}>RECEIPT NUMBER</Text>
          <Text style={pdfStyles.detailValue}>
            {receiptData.receipt_number}
          </Text>
        </View>

        <View style={pdfStyles.detailCell}>
          <Text style={pdfStyles.detailLabel}>PAID BY</Text>
          <Text style={pdfStyles.detailValue}>
            {receiptData.payer_name || "N/A"}
          </Text>
        </View>

        <View style={pdfStyles.detailCell}>
          <Text style={pdfStyles.detailLabel}>PAYMENT CURRENCY</Text>
          <Text style={pdfStyles.detailValue}>
            {receiptData.payment_currency === "USD" ? "USD" : "Liberian Dollar (LD)"}
          </Text>
        </View>

        <View style={pdfStyles.detailCell}>
          <Text style={pdfStyles.detailLabel}>PAYMENT DATE</Text>
          <Text style={pdfStyles.detailValue}>
            {receiptData.date}
          </Text>
        </View>

        {receiptData.payment_currency === "USD" ? (
          <View style={pdfStyles.detailCell}>
            <Text style={pdfStyles.detailLabel}>EXCHANGE RATE</Text>
            <Text style={pdfStyles.detailValue}>
              1 USD = LD {formatMoney(receiptData.exchange_rate)}
            </Text>
          </View>
        ) : null}

        {receiptData.payment_currency === "USD" ? (
          <View style={pdfStyles.detailCell}>
            <Text style={pdfStyles.detailLabel}>LRD EQUIVALENT</Text>
            <Text style={pdfStyles.detailValue}>
              {receiptData.amount_paid_lrd}
            </Text>
          </View>
        ) : null}
      </View>

      <View style={pdfStyles.servicesBox}>
        <Text style={pdfStyles.servicesLabel}>
          PAYMENT SERVICES / FEE ALLOCATION
        </Text>

        <Text style={pdfStyles.servicesText}>
          {receiptData.services || "No fee allocation recorded"}
        </Text>
      </View>

      <View style={pdfStyles.amountSection}>
        <View style={[pdfStyles.amountBox, pdfStyles.amountDue]}>
          <Text style={pdfStyles.amountLabel}>AMOUNT DUE</Text>
          <Text style={pdfStyles.amountValue}>
            {receiptData.amount_due}
          </Text>
        </View>

        <View style={[pdfStyles.amountBox, pdfStyles.amountPaid]}>
          <Text style={[pdfStyles.amountLabel, pdfStyles.amountLabelPaid]}>
            AMOUNT PAID
          </Text>

          <Text style={[pdfStyles.amountValue, pdfStyles.amountValuePaid]}>
            {receiptData.amount_paid}
          </Text>

          {receiptData.payment_currency === "USD" ? (
            <Text style={pdfStyles.amountSubValue}>
              LRD equivalent: {receiptData.amount_paid_lrd}
            </Text>
          ) : null}
        </View>

        <View
          style={[
            pdfStyles.amountBox,
            pdfStyles.amountBalance,
            pdfStyles.amountBoxLast,
          ]}
        >
          <Text style={pdfStyles.amountLabel}>REMAINING BALANCE</Text>
          <Text style={pdfStyles.amountValue}>
            {receiptData.balance}
          </Text>
        </View>
      </View>

      <View style={pdfStyles.footer}>
        <Text style={pdfStyles.footerText}>
          Date issued: {receiptData.date}{"\n"}
          Keep this receipt for your records.
        </Text>

        <View style={pdfStyles.signature}>
          <View style={pdfStyles.signatureLine} />
          <Text style={pdfStyles.signatureText}>
            Authorized Finance Officer
          </Text>
        </View>
      </View>

      <Text style={pdfStyles.reference}>
        Receipt Reference: {receiptData.receipt_number}
      </Text>

      <Text style={pdfStyles.officialFooter}>
        Official School Payment Receipt | Polu Integrated School Management System
      </Text>
    </View>
  );
};

/* =========================================================
   PDF DOCUMENT — BOTH COPIES ON ONE A4 PAGE
========================================================= */

const ReceiptPDF = ({ receiptData, qrCodeUrl }) => (
  <Document
    title={`Payment Receipt ${receiptData.receipt_number}`}
    author={SCHOOL_NAME}
    subject="Official Student Payment Receipt"
  >
    <Page size="A4" style={pdfStyles.page} wrap={false}>
      <ReceiptCopyPDF
        copyType="STUDENT COPY"
        receiptData={receiptData}
        qrCodeUrl={qrCodeUrl}
      />

      <View style={pdfStyles.divider}>
        <Text
          style={{
            fontSize: 6,
            color: COLORS.muted,
            textAlign: "center",
            marginTop: 3,
          }}
        >
          CUT HERE — STUDENT COPY ABOVE / FINANCE COPY BELOW
        </Text>
      </View>

      <ReceiptCopyPDF
        copyType="FINANCE COPY"
        receiptData={receiptData}
        qrCodeUrl={qrCodeUrl}
      />
    </Page>
  </Document>
);

/* =========================================================
   HTML PREVIEW STYLES
========================================================= */

const preview = {
  page: {
    fontFamily: "Arial, Helvetica, sans-serif",
    color: COLORS.text,
    background: COLORS.white,
  },

  receipt: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 16,
    background: COLORS.white,
    marginBottom: 14,
    boxShadow: "0 3px 12px rgba(6,42,75,0.05)",
    breakInside: "avoid",
  },

  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderBottom: `3px solid ${COLORS.navy}`,
    paddingBottom: 12,
  },

  logo: {
    width: 60,
    height: 60,
    objectFit: "contain",
    flexShrink: 0,
  },

  schoolName: {
    fontSize: 16,
    fontWeight: 800,
    color: COLORS.navy,
    marginBottom: 5,
    lineHeight: 1.25,
  },

  schoolAddress: {
    fontSize: 10,
    color: COLORS.muted,
    lineHeight: 1.5,
  },

  contact: {
    fontSize: 10,
    color: COLORS.muted,
    marginTop: 3,
  },

  officialBadge: {
    marginLeft: "auto",
    flexShrink: 0,
    background: COLORS.navy,
    color: COLORS.gold,
    borderRadius: 5,
    padding: "8px 10px",
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.5,
    textAlign: "center",
    lineHeight: 1.5,
  },

  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 0",
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: 12,
  },

  copyTitle: {
    fontSize: 14,
    color: COLORS.navy,
    fontWeight: 800,
    letterSpacing: 1,
  },

  installment: {
    fontSize: 10,
    color: COLORS.deepBlue,
    fontWeight: 700,
    textAlign: "right",
  },

  studentSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingBottom: 12,
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: 12,
  },

  studentDetails: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    flex: 1,
  },

  avatar: {
    width: 62,
    height: 62,
    borderRadius: "50%",
    objectFit: "cover",
    border: `2px solid ${COLORS.gold}`,
    flexShrink: 0,
  },

  studentName: {
    fontSize: 15,
    color: COLORS.navy,
    fontWeight: 800,
    marginBottom: 6,
  },

  studentLine: {
    fontSize: 10,
    lineHeight: 1.65,
    color: COLORS.text,
  },

  label: {
    fontWeight: 700,
    color: COLORS.deepBlue,
  },

  qrCode: {
    width: 68,
    height: 68,
    objectFit: "contain",
    flexShrink: 0,
  },

  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    borderTop: `1px solid ${COLORS.border}`,
    borderLeft: `1px solid ${COLORS.border}`,
    marginBottom: 12,
  },

  detailCell: {
    padding: "9px 10px",
    borderRight: `1px solid ${COLORS.border}`,
    borderBottom: `1px solid ${COLORS.border}`,
    minWidth: 0,
  },

  detailLabel: {
    fontSize: 9,
    color: COLORS.muted,
    fontWeight: 700,
    marginBottom: 5,
    letterSpacing: 0.3,
  },

  detailValue: {
    fontSize: 11,
    color: COLORS.text,
    fontWeight: 700,
    overflowWrap: "anywhere",
    lineHeight: 1.4,
  },

  services: {
    background: COLORS.paper,
    borderLeft: `4px solid ${COLORS.goldenYellow}`,
    padding: 10,
    marginBottom: 12,
    borderRadius: "0 5px 5px 0",
  },

  servicesTitle: {
    fontSize: 10,
    fontWeight: 800,
    color: COLORS.navy,
    marginBottom: 5,
  },

  servicesText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: COLORS.text,
    overflowWrap: "anywhere",
  },

  amountSection: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    marginBottom: 12,
  },

  amountBox: {
    minWidth: 0,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: "11px 6px",
    textAlign: "center",
  },

  due: {
    background: COLORS.paleBlue,
    borderColor: COLORS.deepBlue,
  },

  paid: {
    background: COLORS.navy,
    borderColor: COLORS.navy,
    color: COLORS.white,
  },

  balance: {
    background: COLORS.paleGold,
    borderColor: COLORS.goldenYellow,
  },

  amountLabel: {
    fontSize: 9,
    fontWeight: 800,
    marginBottom: 7,
    letterSpacing: 0.3,
  },

  amountValue: {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.3,
    overflowWrap: "anywhere",
  },

  amountSubValue: {
    fontSize: 9,
    marginTop: 5,
    color: COLORS.white,
    lineHeight: 1.4,
  },

  footer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: 10,
    borderTop: `1px solid ${COLORS.border}`,
  },

  footerText: {
    fontSize: 9,
    color: COLORS.muted,
    lineHeight: 1.6,
  },

  signature: {
    width: 165,
    maxWidth: "45%",
    flexShrink: 0,
    textAlign: "center",
  },

  signatureLine: {
    borderBottom: `1px solid ${COLORS.navy}`,
    height: 24,
    marginBottom: 5,
  },

  signatureText: {
    fontSize: 9,
    color: COLORS.deepBlue,
    fontWeight: 700,
  },

  reference: {
    fontSize: 9,
    color: COLORS.muted,
    marginTop: 9,
    overflowWrap: "anywhere",
  },

  officialFooter: {
    borderTop: `1px solid ${COLORS.border}`,
    marginTop: 8,
    paddingTop: 7,
    textAlign: "center",
    fontSize: 8,
    color: COLORS.muted,
    lineHeight: 1.4,
  },

  cutDivider: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: COLORS.muted,
    fontSize: 9,
    fontWeight: 700,
    borderTop: `1px dashed ${COLORS.muted}`,
    margin: "18px 0",
    paddingTop: 8,
    textAlign: "center",
  },
};

/* =========================================================
   HTML RECEIPT COPY
========================================================= */

const ReceiptCopyPreview = ({
  copyType,
  receiptData,
  qrCodeUrl,
}) => {
  const isUsd = receiptData.payment_currency === "USD";

  return (
    <div style={preview.receipt}>
      <div style={preview.header}>
        <img
          src={SchoolLogo}
          alt="School logo"
          style={preview.logo}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={preview.schoolName}>{SCHOOL_NAME}</div>

          <div style={preview.schoolAddress}>
            {SCHOOL_ADDRESS}
          </div>

          <div style={preview.contact}>
            Contact: {SCHOOL_CONTACT}
          </div>
        </div>

        <div style={preview.officialBadge}>
          OFFICIAL
          <br />
          RECEIPT
        </div>
      </div>

      <div style={preview.titleRow}>
        <div style={preview.copyTitle}>{copyType}</div>

        <div style={preview.installment}>
          {receiptData.last_installment || "Payment"} PAYMENT
        </div>
      </div>

      <div style={preview.studentSection}>
        <div style={preview.studentDetails}>
          <img
            src={receiptData.profile_image || AvatarStudent}
            alt="Student"
            style={preview.avatar}
          />

          <div style={{ minWidth: 0 }}>
            <div style={preview.studentName}>
              {receiptData.student_name || "Student"}
            </div>

            <div style={preview.studentLine}>
              <span style={preview.label}>Class: </span>
              {receiptData.grade_class_name || "N/A"}
            </div>

            <div style={preview.studentLine}>
              <span style={preview.label}>Student Type: </span>
              {receiptData.student_type || "N/A"}
            </div>

            <div style={preview.studentLine}>
              <span style={preview.label}>Yearly Fee: </span>
              {receiptData.yearly_fee}
            </div>

            <div style={preview.studentLine}>
              <span style={preview.label}>Overall Balance: </span>
              {receiptData.balance_fee}
            </div>
          </div>
        </div>

        {qrCodeUrl ? (
          <img
            src={qrCodeUrl}
            alt="Receipt verification QR code"
            style={preview.qrCode}
          />
        ) : null}
      </div>

      <div style={preview.detailsGrid}>
        <div style={preview.detailCell}>
          <div style={preview.detailLabel}>RECEIPT NUMBER</div>
          <div style={preview.detailValue}>
            {receiptData.receipt_number}
          </div>
        </div>

        <div style={preview.detailCell}>
          <div style={preview.detailLabel}>PAID BY</div>
          <div style={preview.detailValue}>
            {receiptData.payer_name || "N/A"}
          </div>
        </div>

        <div style={preview.detailCell}>
          <div style={preview.detailLabel}>PAYMENT CURRENCY</div>
          <div style={preview.detailValue}>
            {isUsd ? "USD" : "Liberian Dollar (LD)"}
          </div>
        </div>

        <div style={preview.detailCell}>
          <div style={preview.detailLabel}>PAYMENT DATE</div>
          <div style={preview.detailValue}>{receiptData.date}</div>
        </div>

        {isUsd ? (
          <div style={preview.detailCell}>
            <div style={preview.detailLabel}>EXCHANGE RATE</div>
            <div style={preview.detailValue}>
              1 USD = LD {formatMoney(receiptData.exchange_rate)}
            </div>
          </div>
        ) : null}

        {isUsd ? (
          <div style={preview.detailCell}>
            <div style={preview.detailLabel}>LRD EQUIVALENT</div>
            <div style={preview.detailValue}>
              {receiptData.amount_paid_lrd}
            </div>
          </div>
        ) : null}
      </div>

      <div style={preview.services}>
        <div style={preview.servicesTitle}>
          PAYMENT SERVICES / FEE ALLOCATION
        </div>

        <div style={preview.servicesText}>
          {receiptData.services || "No fee allocation recorded"}
        </div>
      </div>

      <div style={preview.amountSection}>
        <div style={{ ...preview.amountBox, ...preview.due }}>
          <div style={{ ...preview.amountLabel, color: COLORS.deepBlue }}>
            AMOUNT DUE
          </div>
          <div style={{ ...preview.amountValue, color: COLORS.navy }}>
            {receiptData.amount_due}
          </div>
        </div>

        <div style={{ ...preview.amountBox, ...preview.paid }}>
          <div style={{ ...preview.amountLabel, color: COLORS.gold }}>
            AMOUNT PAID
          </div>
          <div style={{ ...preview.amountValue, color: COLORS.white }}>
            {receiptData.amount_paid}
          </div>

          {isUsd ? (
            <div style={preview.amountSubValue}>
              LRD equivalent: {receiptData.amount_paid_lrd}
            </div>
          ) : null}
        </div>

        <div style={{ ...preview.amountBox, ...preview.balance }}>
          <div style={{ ...preview.amountLabel, color: COLORS.deepBlue }}>
            REMAINING BALANCE
          </div>
          <div style={{ ...preview.amountValue, color: COLORS.navy }}>
            {receiptData.balance}
          </div>
        </div>
      </div>

      <div style={preview.footer}>
        <div style={preview.footerText}>
          <div>Date issued: {receiptData.date}</div>
          <div>Keep this receipt for your records.</div>
        </div>

        <div style={preview.signature}>
          <div style={preview.signatureLine} />
          <div style={preview.signatureText}>
            Authorized Finance Officer
          </div>
        </div>
      </div>

      <div style={preview.reference}>
        Receipt Reference: {receiptData.receipt_number}
      </div>

      <div style={preview.officialFooter}>
        Official School Payment Receipt | Polu Integrated School Management System
      </div>
    </div>
  );
};

/* =========================================================
   MAIN RECEIPT DIALOG
========================================================= */

const ReceiptDialog = ({
  open,
  onClose,
  studentDetails,
  currentFeeIndex,
}) => {
  const [receiptData, setReceiptData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  useEffect(() => {
    if (!studentDetails || currentFeeIndex == null) {
      setReceiptData(null);
      setQrCodeUrl("");
      return;
    }

    const fee = studentDetails.fee_records?.[currentFeeIndex];

    if (!fee) {
      setReceiptData(null);
      setQrCodeUrl("");
      return;
    }

    const paymentCurrency = getPaymentCurrency(fee);
    const exchangeRate = getExchangeRate(fee);
    const originalPaymentAmount = getOriginalPaymentAmount(fee);
    const normalizedLrdAmount = getNormalizedLrdAmount(fee);

    const allocations = fee.allocations || [];

    const allocationText = allocations
      .map((allocation) =>
        formatAllocation(
          allocation,
          paymentCurrency,
          exchangeRate
        )
      )
      .filter(Boolean)
      .join(", ");

    const receiptNumber = `REC-${Date.now()}`;

    const data = {
      last_installment: fee.last_installment,
      student_name: studentDetails.student_name,
      grade_class_name: studentDetails.grade_class_name,
      student_type: studentDetails.student_type,
      profile_image: AvatarStudent,

      yearly_fee: formatLrd(fee.yearly_school_fee),
      balance_fee: formatLrd(fee.overall_balance),

      receipt_number: receiptNumber,
      payer_name: fee.payer_name || "N/A",

      payment_currency: paymentCurrency,
      exchange_rate: exchangeRate,

      amount_paid_original: originalPaymentAmount,
      amount_paid_lrd_value: normalizedLrdAmount,

      services: allocationText || "No fee allocation recorded",

      amount_due: formatLrd(fee.fee_charge),

      amount_paid: formatCurrency(
        originalPaymentAmount,
        paymentCurrency
      ),

      amount_paid_lrd: formatLrd(normalizedLrdAmount),
      balance: formatLrd(fee.balance),

      date: new Date(
        fee.created_at || fee.date_paid || Date.now()
      ).toLocaleString(),
    };

    setReceiptData(data);
    setQrCodeUrl("");

    const qrText = [
      "ZION PRAISE ACADEMY - PAYMENT RECEIPT",
      `Receipt ID: ${data.receipt_number}`,
      `Installment: ${data.last_installment || ""}`,
      `Student: ${data.student_name || ""}`,
      `Class: ${data.grade_class_name || ""}`,
      `Payer: ${data.payer_name}`,
      `Currency: ${data.payment_currency}`,
      `Amount Paid: ${data.amount_paid}`,
      data.payment_currency === "USD"
        ? `Exchange Rate: 1 USD = LD ${formatMoney(
            data.exchange_rate
          )}`
        : "",
      data.payment_currency === "USD"
        ? `LRD Equivalent: ${data.amount_paid_lrd}`
        : "",
      `Amount Due: ${data.amount_due}`,
      `Services: ${data.services}`,
      `Balance: ${data.balance}`,
      `Date: ${data.date}`,
    ]
      .filter(Boolean)
      .join("\n");

    let cancelled = false;

    QRCode.toDataURL(qrText, {
      width: 390,
      margin: 1,
      errorCorrectionLevel: "M",
    })
      .then((url) => {
        if (!cancelled) {
          setQrCodeUrl(url);
        }
      })
      .catch((error) => {
        console.error("QR Code Error:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [studentDetails, currentFeeIndex]);

  if (!open || !receiptData) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(4, 18, 32, 0.78)",
        backdropFilter: "blur(5px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1000,
          maxHeight: "95vh",
          overflowY: "auto",
          background: COLORS.paper,
          borderRadius: 12,
          boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
          padding: 22,
        }}
      >
        {/* Dialog heading */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: COLORS.navy,
                marginBottom: 5,
              }}
            >
              Payment Receipt
            </div>

            <div
              style={{
                fontSize: 12,
                color: COLORS.muted,
                lineHeight: 1.6,
              }}
            >
              Review both receipt copies before exporting the official A4 PDF.
            </div>

            <div
              style={{
                display: "inline-block",
                marginTop: 8,
                background: COLORS.paleBlue,
                color: COLORS.deepBlue,
                border: `1px solid ${COLORS.border}`,
                padding: "5px 9px",
                borderRadius: 5,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              Receipt: {receiptData.receipt_number}
            </div>
          </div>

          <div
            style={{
              background: COLORS.navy,
              color: COLORS.gold,
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.5,
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            STUDENT + FINANCE
            <br />
            TWO-COPY RECEIPT
          </div>
        </div>

        {/* Both copies use the same component and data */}
        <div style={preview.page}>
          <ReceiptCopyPreview
            copyType="STUDENT COPY"
            receiptData={receiptData}
            qrCodeUrl={qrCodeUrl}
          />

          <div style={preview.cutDivider}>
            ✂ &nbsp; CUT HERE — STUDENT COPY ABOVE / FINANCE COPY BELOW
          </div>

          <ReceiptCopyPreview
            copyType="FINANCE COPY"
            receiptData={receiptData}
            qrCodeUrl={qrCodeUrl}
          />
        </div>

        {/* Dialog actions */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <div
            style={{
              color: COLORS.muted,
              fontSize: 11,
              lineHeight: 1.5,
              maxWidth: 390,
            }}
          >
            The PDF contains both copies on one A4 page. Use the student
            copy for the payer and retain the finance copy for school records.
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
            }}
          >
            <PDFDownloadLink
              document={
                <ReceiptPDF
                  receiptData={receiptData}
                  qrCodeUrl={qrCodeUrl}
                />
              }
              fileName={`${receiptData.receipt_number}.pdf`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                background: COLORS.navy,
                color: COLORS.white,
                padding: "12px 18px",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 800,
                border: `1px solid ${COLORS.navy}`,
                minHeight: 42,
                boxSizing: "border-box",
              }}
            >
              {({ loading }) =>
                loading ? "Preparing PDF..." : "Export A4 PDF"
              }
            </PDFDownloadLink>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.navy,
                padding: "12px 18px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                minHeight: 42,
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Responsive adjustments for smaller screens */}
      <style>
        {`
          @media (max-width: 640px) {
            .receipt-dialog-preview {
              font-size: 12px;
            }
          }

          @media print {
            body {
              background: white !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ReceiptDialog; 
 


   

  